import { Text, View, StyleSheet, Image, Dimensions } from 'react-native';
import React, { useState, useEffect } from 'react';
import { WpLandingPage } from './../../contexts/campaign';
import Constants from '../../constants';
import ModalBody from './../ModalBody';
import WpModal from './../WpModal';
import { Button, Theme } from './../Button';
import Uppyup from './../Uppyup';
import Input, { Label } from './../Input';
import { useMutation } from "@apollo/client";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Toast from 'react-native-toast-message';
import { UPDATE_LANDING_PAGE } from '../../graphql/campaign';
import CampaignDropdown from '../CampaignDropdown';
import CampaignLabelMapper from '../CampaignLabelMapper';
import { UserContext, WpCampaign } from '../../contexts/user';

const window = Dimensions.get('window');

export default function LandingPageModal({
  isVisible,
  landingPage,
  closeModal,
}: {
  landingPage?: WpLandingPage;
  isVisible?: boolean;
  closeModal: () => void;
}): JSX.Element {
  const { state } = React.useContext(UserContext);
  const [viewWidth, setViewWidth] = useState<number>(0);
  const [newProfile, setNewProfile] = useState<boolean>();
  const [dimensions, setDimensions] = useState(window);

  const [id, setId] = useState<string>();
  const [title, setTitle] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [color, setColor] = useState<string>();
  const [profile, setProfile] = useState<string>();
  const [campaignId, setCampaignId] = useState<string>();
  const [localCampaigns, setLocalCampaigns] = useState<WpCampaign[]>([]);

  const [copyText, setCopyText] = useState<string>('Save and Copy Link');

  const [aspectRatioProfile, setAspectRatioProfile] = useState<number>(0);
  const [ProfileElement, setProfileElement] = useState<JSX.Element>();

  const [updateLandingPage, updateLandingPageData] = useMutation(
    UPDATE_LANDING_PAGE,
    { fetchPolicy: 'no-cache' }
  );

  // Handle image sizing
  useEffect(() => {
    if (!profile) return;

    let isValid = true;
    Image.getSize(profile, (width, height) => {
      if (isValid) setAspectRatioProfile(width / height);
    });

    return () => {
      isValid = false;
    };
  }, [profile, aspectRatioProfile]);

  // Render image element
  useEffect(() => {
    if (profile) {
      setProfileElement(
        <View style={styles.logo}>
          <Image
            style={[styles.profile, { aspectRatio: 1 }]}
            source={{ uri: profile }}
          />
        </View>
      );
    }
  }, [aspectRatioProfile, profile]);

  // Handle window resize
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setDimensions(window);
    });
    return () => subscription?.remove();
  });

  // Load landing page values into state
  useEffect(() => {
    if (landingPage) {
      if (title === undefined || id !== landingPage.id) {
        setId(landingPage.id);
        setTitle(landingPage.title);
        setDescription(landingPage.description);
        setColor(landingPage.color);
        setProfile(landingPage.image);
        setLocalCampaigns(landingPage.campaigns);
        if (landingPage.campaigns?.length && !Constants.tattooCare) {
          setCampaignId(landingPage.campaigns[0].id);
        }
      }
    }
  }, [landingPage?.id]);

  // Sync updated campaigns after mutation
  useEffect(() => {
    if (updateLandingPageData.data?.updateLandingPage?.success && isVisible) {
      setLocalCampaigns(
        updateLandingPageData.data?.updateLandingPage?.landingPage?.campaigns
      );
    }
  }, [updateLandingPageData.data?.updateLandingPage]);

  const onCloseModal = () => {
    closeModal();
    setCopyText('Save and Copy Link');
  };

  const onUploadProfileComplete = (url: string) => {
    setProfile(url);
    setNewProfile(false);
  };

  const onSave = () => {
    if (landingPage) {
      updateLandingPage({
        variables: {
          landingPageId: landingPage.id,
          landingPage: {
            title,
            description,
            color,
            image: profile,
            campaignId,
          },
        },
      });
      onCloseModal();
    }
  };

  const confirmCopy = () => {
    setCopyText('Copied! Saving...');
    Toast.show({ type: 'success', text1: 'Link Copied' });
    onSave();
  };

  const renderBranding = () => {
    return (
      <View style={{ position: 'relative', zIndex: 2 }}>
        <Input
          name="Landing Page Branding Color"
          style={{ marginBottom: 20 }}
          isColor={true}
          value={color}
          setValue={setColor}
        />
        <Label name="Landing Page Branding Image" />
        {profile && !newProfile ? (
          <View style={{ alignItems: 'center' }}>
            {ProfileElement}
            <Button
              style={{ marginTop: 25 }}
              width={'100%'}
              text="Upload New Brand Image"
              theme={Theme.LINK_PRIMARY}
              onPress={() => setNewProfile(true)}
            />
          </View>
        ) : (
          <Uppyup
            plugins={[]}
            height={200}
            onDone={onCloseModal}
            onUploadComplete={onUploadProfileComplete}
          />
        )}

        {/* Default Campaign Dropdown */}
        <CampaignDropdown
          style={{ marginTop: 15 }}
          optional={false}
          label="Select Campaign"
          info={`This is your default landing page campaign when no surgery's are set`}
          value={campaignId}
          setValue={setCampaignId}
          opener={true}
        />

        {/* Label Mapper for surgeries */}
        <CampaignLabelMapper
          style={{ marginTop: 20 }}
          instantOnly={true}
          optional={false}
          label="Campaign"
          info={`Select a campaign to send out when the landing page is submitted.`}
        />
      </View>
    );
  };

  return (
    <WpModal key="modal-card" isVisible={!!isVisible}>
      <ModalBody title={'Edit Landing Page'} closeModal={onCloseModal}>
        <Input
          style={{ marginBottom: 10 }}
          name="Title"
          placeholder="Enter a title"
          value={title}
          setValue={setTitle}
        />
        <Input
          style={{ marginBottom: 10 }}
          name="Description"
          optional={true}
          multiline={true}
          placeholder="Enter a description"
          value={description}
          setValue={setDescription}
        />

        {renderBranding()}

        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 25,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <CopyToClipboard
            text={`${Constants.app_url}/p/${landingPage?.code}`}
            onCopy={confirmCopy}
          >
            <Text
              style={{
                marginLeft: 5,
                color: Constants.colors.blue,
                cursor: 'pointer',
              }}
            >
              {copyText}
            </Text>
          </CopyToClipboard>
          <Button key="btn-feature-save" text="Save" onPress={onSave} />
        </View>
      </ModalBody>
    </WpModal>
  );
}

const styles = StyleSheet.create({
  pitch: {
    flexDirection: 'row',
    margin: 25,
    paddingLeft: 35,
    borderRadius: 15,
    backgroundColor: '#FFF',
    justifyContent: 'space-evenly',
  },
  header: {
    flex: 2,
    fontSize: 28,
    alignSelf: 'center',
    color: 'rgba(0, 0, 0, 0.7)',
  },
  newBtn: {
    fontFamily: 'GothamMedium',
    fontSize: 11,
    color: Constants.colors.blue,
  },
  controlActive: {
    color: Constants.colors.blue,
    fontFamily: 'GothamMedium',
    fontSize: 14,
    cursor: 'pointer',
    paddingBottom: 10,
    paddingHorizontal: 15,
    borderBottomColor: Constants.colors.blue,
    borderBottomWidth: 1,
  },
  control: {
    color: '#656565',
    fontFamily: 'GothamMedium',
    fontSize: 14,
    cursor: 'pointer',
    paddingBottom: 10,
    paddingHorizontal: 15,
    borderBottomColor: '#FFFFFF00',
    borderBottomWidth: 1,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    gap: 15,
    flexWrap: 'wrap',
    flex: 1,
    position: 'relative',
  },
  column: {
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 300,
    position: 'relative',
  },
  logo: {
    width: 110,
    height: 110,
    backgroundColor: '#FFF',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 55,
    overflow: "hidden",
  },
  profile: {
    width: 110,
    clipPath: 'circle()',
  },
  bubbleWrapper: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 10,
    marginHorizontal: 10,
    overflowX: 'scroll',
  },
  bubble: {
    fontFamily: 'GothamMedium',
    paddingVertical: 13,
    paddingHorizontal: 20,
    borderRadius: 20,
    height: 40,
    color: Constants.colors.darkBlue,
    fontSize: 12,
    backgroundColor: '#F5F7FB',
    marginBottom: 15,
    whiteSpace: 'nowrap',
  },
});

const cssStyles = {
  player: {
    marginTop: 15,
    borderRadius: 15,
    padding: 0,
    overflow: 'hidden',
  },
};

